import React from "react";
import Papa from "papaparse";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "../lib/api";

/**
 * CSV export button.
 *
 * Fetches the live members/leads list from the backend, flattens the fields
 * to a clean row shape, and downloads a CSV via papaparse. Works for both
 * members and leads via the `kind` prop.
 */
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const SHAPERS = {
  member: {
    filename: (gymName) => `${(gymName || "fitnudge").replace(/\s+/g, "_")}-members-${today()}.csv`,
    columns: [
      { key: "name",         header: "Name" },
      { key: "phone",        header: "Phone" },
      { key: "email",        header: "Email" },
      { key: "plan",         header: "Plan" },
      { key: "plan_amount",  header: "Amount" },
      { key: "start_date",   header: "Start date",  fmt: toDate },
      { key: "expiry_date",  header: "Expiry date", fmt: toDate },
      { key: "last_visit",   header: "Last visit",  fmt: toDate },
      { key: "checkins",     header: "Total check-ins", from: (m) => Array.isArray(m.check_ins) ? m.check_ins.length : 0 },
      { key: "risk_score",   header: "Risk score" },
      { key: "status",       header: "Status" },
      { key: "notes",        header: "Notes" },
    ],
    load: () => endpoints.listMembers(),
  },
  lead: {
    filename: (gymName) => `${(gymName || "fitnudge").replace(/\s+/g, "_")}-leads-${today()}.csv`,
    columns: [
      { key: "name",         header: "Name" },
      { key: "phone",        header: "Phone" },
      { key: "source",       header: "Source" },
      { key: "interest",     header: "Interest" },
      { key: "status",       header: "Stage" },
      { key: "trial_date",   header: "Trial date", fmt: toDate },
      { key: "trial_status", header: "Trial status" },
      { key: "created_at",   header: "Created",    fmt: toDate },
      { key: "notes",        header: "Notes" },
    ],
    load: () => endpoints.listLeads(),
  },
};

export default function CsvExportButton({ kind = "member", gymName, className, size = "md" }) {
  const [busy, setBusy] = React.useState(false);
  const shaper = SHAPERS[kind];

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await shaper.load();
      const rows = Array.isArray(data) ? data : [];
      if (rows.length === 0) {
        toast.info(`No ${kind}s to export yet.`);
        setBusy(false);
        return;
      }
      const csvRows = rows.map(r => {
        const out = {};
        for (const col of shaper.columns) {
          const raw = col.from ? col.from(r) : r[col.key];
          const val = raw === undefined || raw === null ? "" : raw;
          out[col.header] = col.fmt ? col.fmt(val) : String(val);
        }
        return out;
      });
      const csv = Papa.unparse(csvRows, { columns: shaper.columns.map(c => c.header) });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = shaper.filename(gymName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} ${kind}${rows.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const sizeCls = size === "sm"
    ? "px-3 py-1.5 text-xs"
    : "px-4 py-2.5 text-sm";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`csv-export-${kind}-btn`}
      className={`inline-flex items-center gap-2 bg-card border border-border hover:bg-muted/50 text-foreground font-medium rounded-lg transition-colors disabled:opacity-60 ${sizeCls} ${className || ""}`}
    >
      <Download className="w-4 h-4" /> {busy ? "Exporting…" : "Export CSV"}
    </button>
  );
}
